import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";
import { clearAuthCallbackUrl, readSupabaseTokensFromUrl } from "@/lib/auth";
import { logout } from "@/lib/api";
import { clearLegacySessionToken, exchangeBackendSession } from "@/lib/session";
import { useSession } from "@/context/SessionContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshSession } = useSession();
  const [error, setError] = useState(null);
  const [canRetry, setCanRetry] = useState(false);
  const [status, setStatus] = useState("Completing sign in...");
  const [retryNonce, setRetryNonce] = useState(0);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const run = async () => {
      let phase = "provider";
      try {
        console.log("[AuthCallback] Start. Path:", `${window.location.origin}${window.location.pathname}`);

        // ── Step 1: Get Supabase session from PKCE code ──
        const code = new URLSearchParams(window.location.search).get("code");
        const { accessToken, refreshToken, errorDescription, hasTokens } = readSupabaseTokensFromUrl();
        let session = null;

        if (code) {
          setStatus("Exchanging authorization code...");
          console.log("[AuthCallback] PKCE code found, exchanging...");

          // Clean URL immediately to prevent any re-processing
          clearAuthCallbackUrl();

          const { data, error: codeErr } = await supabase.auth.exchangeCodeForSession(code);
          if (codeErr) {
            console.error("[AuthCallback] Code exchange error:", codeErr.message);
          } else {
            session = data?.session;
            console.log("[AuthCallback] Code exchange OK:", session?.user?.email);
          }
        }

        if (!session && errorDescription) {
          setCanRetry(false);
          setError(decodeURIComponent(errorDescription));
          clearAuthCallbackUrl();
          return;
        }

        if (!session && hasTokens) {
          setStatus("Restoring session...");
          console.log("[AuthCallback] Hash tokens found, restoring session...");
          clearAuthCallbackUrl();

          const { data, error: tokenErr } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (tokenErr) {
            console.error("[AuthCallback] Hash token restore error:", tokenErr.message);
          } else {
            session = data?.session;
            console.log("[AuthCallback] Hash token restore OK:", session?.user?.email);
          }
        }

        // Fallback: check existing session
        if (!session) {
          console.log("[AuthCallback] Checking existing session...");
          const { data } = await supabase.auth.getSession();
          session = data?.session;
          console.log("[AuthCallback] getSession:", session ? session.user?.email : "none");
        }

        if (!session) {
          setCanRetry(false);
          setError("No session after login. Add this URL to Supabase Redirect URLs.");
          return;
        }

        // ── Step 2: Create backend session ──
        // Small delay to let browser settle after redirect chain
        await new Promise(r => setTimeout(r, 200));
        setStatus("Creating app session...");
        phase = "backend";

        console.log("[AuthCallback] Calling /api/auth/session...");
        const { status: resStatus, data: resData } = await exchangeBackendSession(
          session.access_token,
          sessionStorage.getItem("pending_invite_token")
        );
        console.log("[AuthCallback] session response:", resStatus);

        // ── Step 3: Handle response ──
        if (resStatus !== 200) {
          const detail = resData?.detail || `Backend returned ${resStatus}`;
          console.error("[AuthCallback] Backend rejected:", detail);
          setCanRetry(resStatus === 429 || resStatus >= 500);
          setError(detail);
          return;
        }

        console.log("[AuthCallback] Session created for:", resData?.user?.email);
        clearLegacySessionToken();
        sessionStorage.removeItem("pending_invite_token");

        if (!(await refreshSession())) {
          setCanRetry(true);
          setError(
            "Signed in with Google but the app session cookie was not saved. " +
              "On Cloudflare: remove REACT_APP_BACKEND_URL from build env. " +
              "On Railway: set BEHIND_PROXY=true and ENVIRONMENT=production, then redeploy both."
          );
          return;
        }

        navigate("/dashboard", { replace: true });

      } catch (e) {
        console.error("[AuthCallback] Fatal:", e);
        setCanRetry(phase === "backend");
        setError(e.message || "Auth callback failed");
      }
    };

    run();
  }, [navigate, refreshSession, retryNonce]);

  const retrySessionExchange = () => {
    setError(null);
    setStatus("Retrying app session...");
    hasProcessed.current = false;
    setRetryNonce((value) => value + 1);
  };

  const restartSignIn = async () => {
    try {
      await logout();
      await supabase.auth.signOut();
    } catch (signOutError) {
      console.warn("[AuthCallback] Clean sign out failed:", signOutError);
    } finally {
      navigate("/login", { replace: true });
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-6">
          <h1 className="text-2xl font-bold text-red-400 mb-4">Login Failed</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <p className="text-xs text-muted-foreground/60 mb-6">
            Your provider login is preserved, so retrying does not restart sign in.
          </p>
          <div className="flex gap-3 justify-center">
            {canRetry && (
              <button onClick={retrySessionExchange}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm">
                Try Again
              </button>
            )}
            <button onClick={restartSignIn}
              className="px-4 py-2 border border-border rounded-lg text-sm">
              Sign In Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <span className="text-muted-foreground">{status}</span>
      </div>
    </div>
  );
}
